/**
 * Hook for analytics view data calculations
 */

import { useSyncedStore } from '@syncedstore/react'
import { todoStore } from 'src/store/todo'
import type { TaskType, Worker } from 'src/store/todo/types'
import { calculatePercentage, formatNumber } from '../utils'

/**
 * Worker harvest data structure
 */
type WorkerHarvestData = {
  worker: Worker
  totalHVSTBoxes: number
  workerPercentage: number
}

/**
 * Worker task completion data structure
 */
type WorkerTaskData = {
  worker: Worker
  completedLocations: number
}

/**
 * Custom hook to calculate analytics data for harvest and other tasks
 *
 * @param selectedTeamId - ID of the currently selected team
 * @param workers - Array of workers for the selected team
 * @returns Object with harvest and non-harvest task analytics
 */
export const useAnalyticsData = (selectedTeamId: string | null, workers: Worker[]) => {
  const state = useSyncedStore(todoStore)
  const tasks = (state.taskTypes || []) as TaskType[]

  /**
   * Calculate harvest (HVST) analytics
   */
  const getHarvestAnalytics = () => {
    const hvstTasks = tasks.filter((t: TaskType) => t.taskTypeMoniker === 'HVST')

    // Calculate team total boxes
    const teamTotalBoxes = state.workerAssignmentLogs
      .filter(
        (log: any) =>
          hvstTasks.some((t: TaskType) => t.id === log.taskTypeId) &&
          log.teamId === selectedTeamId &&
          !log.softDeleted
      )
      .reduce((sum: number, log: any) => sum + log.value, 0)

    // Get workers with harvest assignments
    const harvestWorkers: WorkerHarvestData[] = workers
      .map((worker: any) => {
        const workerAssignments = state.workerAssignments.filter(
          (a) =>
            a.workerId === worker.id &&
            a.teamId === selectedTeamId &&
            !a.softDeleted &&
            hvstTasks.some((t: TaskType) => t.id === a.taskTypeId)
        )

        if (workerAssignments.length === 0) return null

        const totalHVSTBoxes = state.workerAssignmentLogs
          .filter(
            (log) =>
              log.workerId === worker.id &&
              hvstTasks.some((t: TaskType) => t.id === log.taskTypeId) &&
              log.teamId === selectedTeamId &&
              !log.softDeleted
          )
          .reduce((sum, log) => sum + log.value, 0)

        const workerPercentage = calculatePercentage(totalHVSTBoxes, teamTotalBoxes)

        return {
          worker,
          totalHVSTBoxes,
          workerPercentage,
        }
      })
      .filter((data): data is WorkerHarvestData => data !== null)
      .sort((a, b) => b.totalHVSTBoxes - a.totalHVSTBoxes)

    return {
      hvstTasks,
      teamTotalBoxes,
      harvestWorkers,
      formattedTotal: formatNumber(teamTotalBoxes),
    }
  }

  /**
   * Calculate non-harvest task analytics
   */
  const getNonHarvestAnalytics = () => {
    const nonHvstTasks = tasks.filter((t: TaskType) => t.taskTypeMoniker !== 'HVST')

    return nonHvstTasks.map((task: TaskType) => {
      // Calculate total completed locations for this task
      const taskTotalCompleted = state.workerAssignmentLogs.filter(
        (log: any) =>
          log.taskTypeId === task.id && log.teamId === selectedTeamId && !log.softDeleted
      ).length

      // Get workers with assignments for this task
      const workersForTask: WorkerTaskData[] = workers
        .map((worker: any) => {
          const workerAssignments = state.workerAssignments.filter(
            (a) =>
              a.workerId === worker.id &&
              a.taskTypeId === task.id &&
              a.teamId === selectedTeamId &&
              !a.softDeleted
          )

          if (workerAssignments.length === 0) return null

          // Count completed LOCATIONS from WorkerAssignmentLogs
          const completedLocations = state.workerAssignmentLogs.filter(
            (log: any) =>
              log.workerId === worker.id &&
              log.taskTypeId === task.id &&
              log.teamId === selectedTeamId &&
              !log.softDeleted
          ).length

          if (completedLocations === 0) return null

          return {
            worker,
            completedLocations,
          }
        })
        .filter((data): data is WorkerTaskData => data !== null)

      return {
        task,
        taskTotalCompleted,
        workersForTask,
      }
    })
  }

  return {
    getHarvestAnalytics,
    getNonHarvestAnalytics,
  }
}
